import React, { useContext, ReactNode, useEffect, useState } from "react";
import { useTracking } from "react-tracking";
import { TableContext } from "common/components/Table/TableProvider";
import { TableRowEditor } from "common/components/Table/TableRowEditor";
import { TableRowContext, TableRowProvider } from "common/components/Table/TableRowProvider";
import { List } from "immutable";
import { AclKeys, AclValues } from "common/records/User";
import { DropdownOption } from "common/components/DropdownMenu";
import { TrackingData } from "common/helpers/Analytics";

// Define the allowed action buttons in a type-safe way
export const ALLOWED_ACTION_BUTTONS = [
  "activate",
  "collapse_content",
  "delete",
  "description",
  "download",
  "edit",
  "north_east",
  "paid",
  "play_arrow",
  "work",
  "save",
  "cached",
  "more",
  "file_copy",
  "add",
  "email",
  "magic_wand",
  "import",
  "mail",
  "signature",
  "laptop",
  "email",
  "rule",
  "history_edu",
  "block",
  "info",
] as const;
export type ActionButton =
  | {
      kind: (typeof ALLOWED_ACTION_BUTTONS)[number];
      onClick: () => void;
      acl?: [AclKeys, AclValues];
      tooltipTitle?: string;
      disabled?: boolean;
      analyticsAction?: string;
      /**
       * Any additional data to be sent to analytics on click
       */
      analyticsData?: Partial<TrackingData>;
    }
  | MoreActionButton;

export interface MoreActionButton {
  kind: "more";
  actions: DropdownOption[];
  acl?: [AclKeys, AclValues];
  tooltipTitle?: string;
  disabled?: boolean;
  analyticsAction?: string;
  onClick?: never;
  /**
   * Any additional data to be sent to analytics on click
   */
  analyticsData?: Partial<TrackingData>;
}

export interface ITableRowProps {
  accordionContent?: React.JSX.Element;
  actionButtons?: List<ActionButton>;
  bg?: string;
  children: ReactNode;
  dragHandle?: Object;
  height?: "sm" | "md" | "lg" | "min-height";
  padding?: string;
  hover?: boolean;
  dataTest?: string;
  persistentContent?: ReactNode;
  /**
   * Makes the whole row clickable. Additive and default-off: a row without `onClick` renders exactly
   * as before (no cursor/role/tabIndex/keydown). Action-button and accordion-toggle clicks stop
   * propagation, so they don't fire the row `onClick`.
   *
   * Accessible whole-row button semantics (role="button", `tabIndex`, Enter/Space, focus ring) are
   * applied only when the row has **no** interactive children — nesting action buttons or an accordion
   * toggle inside a button-role row is invalid ARIA. A row that combines `onClick` with
   * `actionButtons`/`accordionContent` gets a plain mouse click instead, leaving those children as the
   * keyboard-reachable controls.
   */
  onClick?: () => void;
  /** Analytics action fired on row activation (only when `onClick` is set). */
  analyticsAction?: string;
  /** Extra analytics payload merged into the row-activation event. */
  analyticsData?: Partial<TrackingData>;
}

export const Tr: React.FC<ITableRowProps> = (props) => {
  return (
    <TableRowProvider>
      <TableRow {...props}></TableRow>
    </TableRowProvider>
  );
};

const bgClass = (bg: string | undefined): string => {
  if (!bg || !bg.startsWith("bg-")) {
    return "";
  }

  return bg;
};

const heightClass = (hasImage: boolean, height: ITableRowProps["height"]) => {
  if (hasImage) {
    return "h-[64px]";
  }

  switch (height) {
    case "sm":
      return "h-[40px]";
    case "lg":
      return "h-[60px]";
    case "min-height":
      return "min-h-[56px]";
    default:
      return "h-[56px]";
  }
};

const hoverClass = (
  isHeaderRow: boolean,
  tableHoverOn: boolean,
  bg: ITableRowProps["bg"],
  rowHoverOn: boolean,
): string => {
  if (isHeaderRow || bg || !tableHoverOn || !rowHoverOn) {
    return "";
  }
  return "hover:bg-secondary-50";
};

const TableRow: React.FC<ITableRowProps> = ({
  accordionContent,
  actionButtons = List(),
  children,
  dragHandle,
  height = "md",
  bg,
  padding = "xs:px-[4px] md:px-[8px] xl:px-[38px]",
  hover = true,
  dataTest,
  persistentContent,
  onClick,
  analyticsAction,
  analyticsData,
}) => {
  const { isHeaderRow, hasImage } = useContext(TableRowContext);
  const { gridColsClass, hover: tableHover, sortable, setSortable } = useContext(TableContext);
  const { trackEvent } = useTracking();
  const [accordionOpen, setAccordionOpen] = useState(false);
  // A clickable row gets a plain mouse onClick. Accessible whole-row button semantics
  // (role/tabIndex/keyboard/focus ring) are added only when the row has no interactive children of
  // its own — action buttons or an accordion toggle nested inside a role="button" row is invalid
  // ARIA, so such a row keeps those children as its keyboard-reachable controls.
  const hasInteractiveChildren = !actionButtons.isEmpty() || !!accordionContent;
  const clickable = !!onClick && !isHeaderRow;
  const asButton = clickable && !hasInteractiveChildren;
  const ownClassName = `grid group/tr relative border-b border-b-secondary-100
  ${bgClass(bg)}
  ${sortable ? "xs:px-[28px] xl:px-[38px]" : padding}
  ${gridColsClass}
  ${heightClass(hasImage, height)}
  ${hoverClass(isHeaderRow, tableHover, bg, hover)}
  ${clickable ? "cursor-pointer" : ""}
  ${asButton ? "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-300" : ""}`;

  const activate = () => {
    if (!onClick) return;
    if (analyticsAction) {
      trackEvent({ action: analyticsAction, ...analyticsData });
    }
    onClick();
  };

  useEffect(() => {
    if (dragHandle && !sortable) {
      setSortable(true);
    }
  }, [dragHandle]);

  return (
    <>
      <div
        className={ownClassName}
        data-test={dataTest}
        {...(asButton
          ? {
              role: "button",
              tabIndex: 0,
              onClick: activate,
              onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  activate();
                }
              },
            }
          : clickable
            ? { onClick: activate }
            : {})}>
        {children}
        <TableRowEditor
          dragHandle={dragHandle}
          actionButtons={actionButtons}
          accordionContent={accordionContent}
          accordionOpen={accordionOpen}
          persistentContent={persistentContent}
          setAccordionOpen={setAccordionOpen}></TableRowEditor>
      </div>
      {accordionOpen && !isHeaderRow && <div className="border-b border-b-secondary-100">{accordionContent}</div>}
    </>
  );
};
